if (!customElements.get('localization-discovery')) {
  customElements.define(
    'localization-discovery',
    class LocalizationDiscovery extends HTMLElement {
      static STORAGE_KEY = 'localization_discovery_dismissed';

      constructor() {
        super();
        this.suggestion = null;
      }

      connectedCallback() {
        if (window.Shopify?.designMode) return;

        this.config = this.parseConfig();
        this.modal = document.getElementById(`LocalizationDiscoveryModal-${this.dataset.sectionId}`);

        if (!this.modal || !this.config) return;
        if (this.isDismissed()) return;

        this.bindEvents();
        this.fetchSuggestion();
      }

      parseConfig() {
        const configNode = this.querySelector('[data-discovery-config]');

        if (!configNode) return null;

        try {
          return JSON.parse(configNode.textContent);
        } catch (error) {
          console.error('Localization discovery: invalid config', error);
          return null;
        }
      }

      getBrowsingContext() {
        return {
          country: this.dataset.country || window.Shopify?.country || '',
          language: this.dataset.language || window.Shopify?.language || '',
          root: this.getLocalizationRoot(),
        };
      }

      getLocalizationRoot() {
        const root = this.dataset.localizationRoot || window.Shopify?.routes?.root || '/';
        return root.endsWith('/') ? root : `${root}/`;
      }

      bindEvents() {
        this.modal.querySelector('[data-discovery-accept]')?.addEventListener('click', this.onAccept.bind(this));
        this.modal.querySelector('[data-discovery-dismiss]')?.addEventListener('click', () => this.modal?.hide());

        // ModalDialog dispatches `modalClosed` on document.body (see global.js), not on the
        // modal itself — so we listen globally and only persist the dismissal when *our* modal
        // is the one being closed (it still carries the `open` attribute at dispatch time).
        document.body.addEventListener('modalClosed', () => {
          if (this.modal.hasAttribute('open')) this.setDismissed();
        });
      }

      normalizeCountry(country) {
        return (country || '').toString().trim().toUpperCase();
      }

      normalizeLanguage(language) {
        return (language || '').toString().trim().toLowerCase().split('-')[0];
      }

      parseSuggestion(data, currentCountry, currentLanguage) {
        if (!data.suggestions?.length) return null;

        const suggestion = data.suggestions[0];
        const countryPart = suggestion.parts?.country;
        const languagePart = suggestion.parts?.language;
        const currentCountryNorm = this.normalizeCountry(currentCountry);
        const currentLanguageNorm = this.normalizeLanguage(currentLanguage);

        const suggestedCountry = countryPart?.handle;
        const suggestedLanguage = languagePart?.handle;

        const countryChanges =
          suggestedCountry && this.normalizeCountry(suggestedCountry) !== currentCountryNorm;
        const languageChanges =
          suggestedLanguage && this.normalizeLanguage(suggestedLanguage) !== currentLanguageNorm;

        if (!countryChanges) return null;

        // `handle` = codes pour le POST /localization ; `name` / `options` = libellés d’affichage
        // (dans la langue suggérée si présente — cf. docs Shopify localization-discovery).
        const countryOptions = countryPart?.options || {};
        const languageOptions = languagePart?.options || {};

        return {
          country: countryChanges ? suggestedCountry : currentCountry,
          language: languageChanges
            ? this.normalizeLanguage(suggestedLanguage)
            : currentLanguageNorm,
          countryName:
            countryPart?.name ||
            (countryChanges ? data.detected_values?.country?.name : '') ||
            '',
          languageName: languagePart?.name || '',
          currentCountryName: countryOptions[currentCountryNorm] || '',
          currentLanguageName:
            languageOptions[currentLanguageNorm] ||
            languageOptions[currentLanguage] ||
            '',
        };
      }

      buildSuggestionsUrl(root, country, language) {
        const params = new URLSearchParams();
        params.set('country[enabled]', 'true');
        params.set('country[exclude]', country);
        params.set('language[enabled]', 'true');
        params.set('language[exclude]', language);

        return `${root}browsing_context_suggestions.json?${params.toString()}`;
      }

      async fetchSuggestion() {
        const { country, language, root } = this.getBrowsingContext();

        if (!country || !language) return;

        try {
          const response = await fetch(this.buildSuggestionsUrl(root, country, language));

          if (!response.ok) return;

          const data = await response.json();
          const suggestion = this.parseSuggestion(data, country, language);

          if (!suggestion) return;

          this.suggestion = suggestion;
          this.updateContent();
          this.modal.show();
        } catch (error) {
          console.error('Localization discovery failed', error);
        }
      }

      updateContent() {
        const heading = this.modal.querySelector('[data-discovery-heading]');
        const body = this.modal.querySelector('[data-discovery-body]');
        const flag = this.modal.querySelector('[data-discovery-flag]');
        const accept = this.modal.querySelector('[data-discovery-accept]');
        const dismiss = this.modal.querySelector('[data-discovery-dismiss]');

        if (heading && this.config.heading) {
          heading.innerHTML = this.replacePlaceholders(this.config.heading);
        }

        if (body && this.config.body) {
          body.innerHTML = this.replacePlaceholders(this.config.body);
        }

        if (accept && this.config.acceptLabel) {
          accept.innerHTML = this.replacePlaceholders(this.config.acceptLabel);
        }

        if (dismiss && this.config.dismissLabel) {
          dismiss.innerHTML = this.replacePlaceholders(this.config.dismissLabel);
        }

        if (flag) {
          const emoji = this.countryFlag(this.suggestion.country);
          if (emoji) {
            flag.textContent = emoji;
            flag.hidden = false;
          } else {
            flag.hidden = true;
          }
        }
      }

      replacePlaceholders(content) {
        // Afficher les `name` de l’API, jamais les handles (réservés à submitLocalization).
        const countryName = this.suggestion.countryName || this.dataset.countryName || '';
        const languageName = this.suggestion.languageName || this.dataset.languageName || '';
        const currentCountryName =
          this.suggestion.currentCountryName || this.dataset.countryName || '';
        const currentLanguageName =
          this.suggestion.currentLanguageName || this.dataset.languageName || '';

        return content
          .replace(/\[current_country\]/gi, currentCountryName)
          .replace(/\[current_language\]/gi, currentLanguageName)
          .replace(/\[country\]/gi, countryName)
          .replace(/\[language\]/gi, languageName);
      }

      // Convert a 2-letter ISO country code into its flag emoji (regional indicator symbols).
      countryFlag(iso) {
        const code = (iso || '').toString().trim().toUpperCase();
        if (!/^[A-Z]{2}$/.test(code)) return '';

        return String.fromCodePoint(...[...code].map((char) => 0x1f1e6 - 65 + char.charCodeAt(0)));
      }

      onAccept() {
        if (!this.suggestion) return;

        this.submitLocalization(this.suggestion.country, this.suggestion.language);
      }

      submitLocalization(country, language) {
        const root = this.getLocalizationRoot();
        const formId = `LocalizationDiscoveryForm-${this.dataset.sectionId}`;
        const formHtml = `
          <form id="${formId}" action="${root}localization" method="POST" hidden>
            <input name="_method" value="PUT">
            <input name="country_code" value="${country}">
            <input name="language_code" value="${language}">
          </form>
        `;

        document.body.insertAdjacentHTML('beforeend', formHtml);
        document.getElementById(formId)?.submit();
      }

      isDismissed() {
        const expiry = localStorage.getItem(LocalizationDiscovery.STORAGE_KEY);
        return expiry && Date.now() < Number(expiry);
      }

      setDismissed() {
        const days = Number(this.dataset.dismissDays) || 30;
        const expiry = Date.now() + days * 24 * 60 * 60 * 1000;
        localStorage.setItem(LocalizationDiscovery.STORAGE_KEY, String(expiry));
      }
    }
  );
}
